import React, { useState } from 'react';
import './ZebraGenerator.css';

const ZebraGenerator = () => {
  const [numPersons, setNumPersons] = useState(4);
  const [minimalize, setMinimalize] = useState(true);
  const [seed, setSeed] = useState(Math.floor(Math.random() * 10000));
  const [generatedPuzzle, setGeneratedPuzzle] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Generate persons A, B, C, D, E based on count
  const getPersons = (n) => {
    return Array.from({ length: n }, (_, i) => String.fromCharCode(65 + i));
  };

  // Generate all permutations of an array
  const permutations = (arr) => {
    if (arr.length <= 1) return [arr];
    const result = [];
    for (let i = 0; i < arr.length; i++) {
      const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
      const perms = permutations(rest);
      for (const perm of perms) {
        result.push([arr[i], ...perm]);
      }
    }
    return result;
  };

  // Constraint class
  class Constraint {
    constructor(kind, person, n = null, n1 = null, n2 = null) {
      this.kind = kind;
      this.person = person;
      this.n = n;
      this.n1 = n1;
      this.n2 = n2;
    }

    text() {
      switch (this.kind) {
        case 'eq':
          return `${this.person} is ${this.n}`;
        case 'neq':
          return `${this.person} is not ${this.n}`;
        case 'in':
          return `${this.person} is in range ${this.n1} to ${this.n2}`;
        case 'not_in':
          return `${this.person} is not in range ${this.n1} to ${this.n2}`;
        default:
          throw new Error(`Unknown constraint kind: ${this.kind}`);
      }
    }

    equals(other) {
      return this.kind === other.kind &&
             this.person === other.person &&
             this.n === other.n &&
             this.n1 === other.n1 &&
             this.n2 === other.n2;
    }

    toString() {
      return `${this.kind}-${this.person}-${this.n}-${this.n1}-${this.n2}`;
    }
  }

  // Check if constraint holds for a value
  const constraintHolds = (constraint, value) => {
    switch (constraint.kind) {
      case 'eq':
        return value === constraint.n;
      case 'neq':
        return value !== constraint.n;
      case 'in':
        return constraint.n1 <= value && value <= constraint.n2;
      case 'not_in':
        return !(constraint.n1 <= value && value <= constraint.n2);
      default:
        throw new Error(`Unknown constraint kind: ${constraint.kind}`);
    }
  };

  // Check if assignment satisfies all constraints
  const assignmentSatisfies = (assignment, constraints) => {
    return constraints.every(c => constraintHolds(c, assignment[c.person]));
  };

  // Count solutions consistent with constraints
  const countSolutions = (n, constraints, persons, earlyStop = null) => {
    const numbers = Array.from({ length: n }, (_, i) => i + 1);
    let solCount = 0;
    let uniqueAssignment = null;

    const perms = permutations(numbers);
    
    for (const perm of perms) {
      const assignment = {};
      persons.forEach((person, i) => {
        assignment[person] = perm[i];
      });

      if (assignmentSatisfies(assignment, constraints)) {
        solCount++;
        if (solCount === 1) {
          uniqueAssignment = assignment;
        }
        if (earlyStop && solCount >= earlyStop) {
          return [solCount, null];
        }
      }
    }

    return [solCount, solCount === 1 ? uniqueAssignment : null];
  };

  // Get all valid "in" ranges containing value v
  const allInRangesContaining = (v, n) => {
    const ranges = [];
    for (let l = 1; l <= v; l++) {
      for (let r = v; r <= n; r++) {
        if (l < r) {
          ranges.push([l, r]);
        }
      }
    }
    return ranges;
  };

  // Get all valid "not in" ranges excluding value v
  const allNotInRangesExcluding = (v, n) => {
    const ranges = [];
    for (let l = 1; l <= n; l++) {
      for (let r = l + 1; r <= n; r++) {
        if (!(l <= v && v <= r)) {
          ranges.push([l, r]);
        }
      }
    }
    return ranges;
  };

  // Generate tight 2-wide range around value
  const tightInRangeAround = (v, n, seededRandom) => {
    if (v === 1) return [1, 2];
    if (v === n) return [n - 1, n];
    // v in middle: pick [v-1, v] or [v, v+1]
    return seededRandom() < 0.5 ? [v - 1, v] : [v, v + 1];
  };

  // Generate random constraint consistent with solution
  const randomConstraintConsistentWith = (solution, n, seededRandom, seededRandomChoice, eqAllowed) => {
    const persons = Object.keys(solution);
    let attempts = 0;

    const kinds = ['neq', 'in', 'not_in'];
    if (eqAllowed) kinds.push('eq');
    
    const weights = { neq: 4, in: 3, not_in: 2, eq: 1 };

    while (attempts < 50) {
      attempts++;
      
      // Weighted random choice
      const totalWeight = kinds.reduce((sum, kind) => sum + weights[kind], 0);
      let randomWeight = seededRandom() * totalWeight;
      let selectedKind = null;
      
      for (const kind of kinds) {
        randomWeight -= weights[kind];
        if (randomWeight <= 0) {
          selectedKind = kind;
          break;
        }
      }

      const p = seededRandomChoice(persons);
      const v = solution[p];

      if (selectedKind === 'eq') {
        return new Constraint('eq', p, v);
      }

      if (selectedKind === 'neq') {
        const choices = Array.from({ length: n }, (_, i) => i + 1).filter(x => x !== v);
        if (choices.length === 0) continue;
        return new Constraint('neq', p, seededRandomChoice(choices));
      }

      if (selectedKind === 'in') {
        let candidates = allInRangesContaining(v, n);
        // Avoid full-range most of the time when n >= 3
        if (n >= 3 && seededRandom() < 0.9) {
          candidates = candidates.filter(([l, r]) => !(l === 1 && r === n));
          if (candidates.length === 0) {
            candidates = allInRangesContaining(v, n);
          }
        }
        if (candidates.length === 0) continue;
        const [l, r] = seededRandomChoice(candidates);
        return new Constraint('in', p, null, l, r);
      }

      if (selectedKind === 'not_in') {
        const candidates = allNotInRangesExcluding(v, n);
        if (candidates.length === 0) continue;
        const [l, r] = seededRandomChoice(candidates);
        return new Constraint('not_in', p, null, l, r);
      }
    }

    return null;
  };

  // Generate solution
  const generateSolution = (n, seededRandom) => {
    const people = getPersons(n);
    const nums = Array.from({ length: n }, (_, i) => i + 1);
    
    // Fisher-Yates shuffle
    for (let i = nums.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [nums[i], nums[j]] = [nums[j], nums[i]];
    }

    const solution = {};
    people.forEach((person, i) => {
      solution[person] = nums[i];
    });
    return solution;
  };

  // Main puzzle generator
  const generateZebraPuzzle = (n, seed, minimalize = true) => {
    if (!(3 <= n && n <= 5)) {
      throw new Error("n must be in [3, 4, 5]");
    }

    // Seeded random generator
    let currentSeed = seed;
    const seededRandom = () => {
      const x = Math.sin(currentSeed++) * 10000;
      return x - Math.floor(x);
    };
    const seededRandomChoice = (arr) => arr[Math.floor(seededRandom() * arr.length)];

    const maxRestarts = 10; // Reduced for web performance
    
    for (let restart = 0; restart < maxRestarts; restart++) {
      const solution = generateSolution(n, seededRandom);
      const persons = Object.keys(solution);

      let constraints = [];
      const used = new Set();
      let eqUsed = false;

      let [currCount] = countSolutions(n, constraints, persons);
      const factorial = Array.from({ length: n }, (_, i) => i + 1).reduce((a, b) => a * b, 1);
      
      if (currCount !== factorial) continue;

      let attemptsWithoutProgress = 0;
      let rawAttempts = 0;
      const maxAttempts = 1000; // Reduced for web performance

      while (true) {
        if (currCount === 1) break;
        if (constraints.length > 30) break; // Reduced for web performance
        if (rawAttempts > maxAttempts) break;

        rawAttempts++;

        const proposal = randomConstraintConsistentWith(
          solution, n, seededRandom, seededRandomChoice, !eqUsed
        );

        if (!proposal) {
          attemptsWithoutProgress++;
          continue;
        }

        const proposalKey = proposal.toString();
        if (used.has(proposalKey)) {
          attemptsWithoutProgress++;
          
          // Try targeted tight 'in' occasionally
          if (attemptsWithoutProgress % 25 === 0) {
            const p = seededRandomChoice(persons);
            const v = solution[p];
            const [l, r] = tightInRangeAround(v, n, seededRandom);
            const forced = new Constraint('in', p, null, l, r);
            const forcedKey = forced.toString();
            
            if (!used.has(forcedKey)) {
              const trial = [...constraints, forced];
              const [newCount] = countSolutions(n, trial, persons, 2);
              
              if (newCount < currCount) {
                constraints = trial;
                used.add(forcedKey);
                currCount = newCount;
                attemptsWithoutProgress = 0;
                continue;
              }
            }
          }
          continue;
        }

        const trial = [...constraints, proposal];
        const [newCount] = countSolutions(n, trial, persons, 2);

        if (newCount < currCount) {
          constraints = trial;
          used.add(proposalKey);
          if (proposal.kind === 'eq') eqUsed = true;
          currCount = newCount;
          attemptsWithoutProgress = 0;
        } else {
          used.add(proposalKey);
          attemptsWithoutProgress++;
          
          // Accept neutral clues occasionally
          if (proposal.kind !== 'eq' && seededRandom() < 0.05) {
            constraints = trial;
          }
        }

        // Safety net
        if (attemptsWithoutProgress > 100) {
          const p = seededRandomChoice(persons);
          const v = solution[p];
          
          if (!eqUsed) {
            const hard = new Constraint('eq', p, v);
            const hardKey = hard.toString();
            if (!used.has(hardKey)) {
              constraints = [...constraints, hard];
              used.add(hardKey);
              eqUsed = true;
              [currCount] = countSolutions(n, constraints, persons, 2);
            }
          } else {
            const [l, r] = tightInRangeAround(v, n, seededRandom);
            const hardIn = new Constraint('in', p, null, l, r);
            const hardInKey = hardIn.toString();
            if (!used.has(hardInKey)) {
              constraints = [...constraints, hardIn];
              used.add(hardInKey);
              [currCount] = countSolutions(n, constraints, persons, 2);
            }
          }
          attemptsWithoutProgress = 0;
        }
      }

      const [finalCount, unique] = countSolutions(n, constraints, persons);
      if (finalCount !== 1 || !unique) continue;

      let reduced = false;

      // Optional minimization (simplified for web)
      if (minimalize && constraints.length > 0) {
        // Fisher-Yates shuffle for constraints
        for (let i = constraints.length - 1; i > 0; i--) {
          const j = Math.floor(seededRandom() * (i + 1));
          [constraints[i], constraints[j]] = [constraints[j], constraints[i]];
        }

        let changed = true;
        while (changed) {
          changed = false;
          for (let i = 0; i < constraints.length; i++) {
            const candidate = constraints.filter((_, idx) => idx !== i);
            const [cnt] = countSolutions(n, candidate, persons, 2);
            if (cnt === 1) {
              constraints.splice(i, 1);
              reduced = true;
              changed = true;
              break;
            }
          }
        }
      }

      // Final shuffle
      for (let i = constraints.length - 1; i > 0; i--) {
        const j = Math.floor(seededRandom() * (i + 1));
        [constraints[i], constraints[j]] = [constraints[j], constraints[i]];
      }

      return {
        n: n,
        persons: persons,
        numbers: Array.from({ length: n }, (_, i) => i + 1),
        statements: constraints.map(c => c.text()),
        solution: unique,
        reduced: reduced,
        seed: seed
      };
    }

    throw new Error("Failed to generate a unique-solution puzzle after several restarts");
  };

  // Generate puzzle
  const generatePuzzle = async () => {
    setIsGenerating(true);
    setGeneratedPuzzle(null);
    
    try {
      // Use setTimeout to prevent UI blocking
      setTimeout(() => {
        try {
          const puzzle = generateZebraPuzzle(numPersons, seed, minimalize);
          setGeneratedPuzzle(puzzle);
        } catch (error) {
          console.error('Generation failed:', error);
          alert('Failed to generate puzzle. Try a different seed or parameters.');
        } finally {
          setIsGenerating(false);
        }
      }, 100);
    } catch (error) {
      setIsGenerating(false);
    }
  };

  // Generate with new seed
  const generateWithNewSeed = () => {
    setSeed(Math.floor(Math.random() * 10000));
    setTimeout(() => generatePuzzle(), 0);
  };

  return (
    <div className="zebra-generator">
      <div className="generator-controls">
        <div className="control-section">
          <h3>Number of Persons</h3>
          <div className="person-count-selection">
            {[3, 4, 5].map(count => (
              <label key={count} className="person-count-option">
                <input 
                  type="radio" 
                  name="personCount"
                  value={count}
                  checked={numPersons === count}
                  onChange={(e) => setNumPersons(parseInt(e.target.value))}
                  className="zebra-radio"
                />
                {count} persons ({getPersons(count).join(', ')})
              </label>
            ))}
          </div>
        </div>

        <div className="control-section">
          <h3>Options</h3>
          <label>
            <input 
              type="checkbox" 
              className="zebra-checkbox"
              checked={minimalize}
              onChange={(e) => setMinimalize(e.target.checked)}
            />
            Minimize clues (remove redundant statements)
          </label>
        </div>

        <div className="control-section">
          <h3>Random Seed</h3>
          <input 
            type="number" 
            value={seed}
            onChange={(e) => setSeed(parseInt(e.target.value) || 0)}
            className="seed-input"
          />
        </div>

        <div className="generate-buttons">
          <button 
            onClick={generatePuzzle} 
            className="generate-button"
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
          <button 
            onClick={generateWithNewSeed} 
            className="generate-button secondary"
            disabled={isGenerating}
          >
            New seed and generate
          </button>
        </div>
      </div>

      {generatedPuzzle && (
        <div className="generated-puzzle">
          <div className="puzzle-header">
            <h3>Generated Zebra Puzzle</h3>
            <div className="puzzle-meta">
              <span>Persons: {generatedPuzzle.n}</span>
              <span>Statements: {generatedPuzzle.statements.length}</span>
              <span>Seed: {generatedPuzzle.seed}</span>
              {generatedPuzzle.reduced && <span className="minimized">Minimized</span>}
            </div>
          </div>
          
          <div className="puzzle-description">
            <p><strong>Persons:</strong> {generatedPuzzle.persons.join(', ')}</p>
            <p><strong>Numbers:</strong> {generatedPuzzle.numbers.join(', ')}</p>
            <p><em>Each person is assigned exactly one unique number.</em></p>
          </div>
          
          <div className="statements">
            <h4>Statements:</h4>
            {generatedPuzzle.statements.map((statement, index) => (
              <div key={index} className="statement">
                <span className="statement-number">{index + 1}.</span>
                <span className="statement-text">{statement}</span>
              </div>
            ))}
          </div>
          
          <div className="solutions">
            <h4>Solution:</h4>
            <div className="solution unique-solution">
              <strong>Unique solution:</strong> 
              <span className="world">
                {generatedPuzzle.persons.map(person => 
                  `${person} = ${generatedPuzzle.solution[person]}`
                ).join(', ')}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZebraGenerator;